import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';

// Components
import LandingPage from './components/LandingPage';
import HomeScreen from './components/HomeScreen';
import SignUp from './components/SignUp';
import SignIn from './components/Login';
import ProfileScreen from './components/ProfileScreen';
import MessagesScreen from './components/MessagesScreen';
import MatchesScreen from './components/MatchesScreen';
import ChatScreen from './components/ChatScreen';
import SettingsScreen from './components/SettingsScreen';
import PaymentResponseScreen from './components/PaymentResponseScreen';
import CybersourcePostHandler from './CybersourcePostHandler';
import AdminPortal from './components/AdminPortal';
import UserDetails from './components/UserDetails';
import Post from './components/Post';
import TermsAndConditions from './components/TermsandConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import PaymentInstructions from './components/PaymentInstructions';

import './App.css';

// --- SHARED AUTH HOOK ---
const useAuth = () => {
  const [user, setUser] = React.useState(null);
  const [userData, setUserData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Force refresh the user token to get the latest emailVerified status
        await firebaseUser.reload();
        const updatedUser = auth.currentUser;
        setUser(updatedUser);

        try {
          const docSnap = await getDoc(doc(db, 'users', updatedUser.uid));
          if (docSnap.exists()) {
            setUserData(docSnap.data());
          }
        } catch (err) {
          console.error("Error fetching user data:", err);
        }
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const isEmailVerified = user?.emailVerified;
  // Account is activated if status is premium or active
  const isActivated = userData?.status === 'premium' || userData?.status === 'active';

  return { user, userData, loading, isEmailVerified, isActivated };
};

// --- ROUTE GUARDS ---

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return !user ? children : <Navigate to="/home" replace />;
};

const AuthRoute = ({ children }) => {
  const { user, loading, isEmailVerified, isActivated } = useAuth();
  const location = useLocation();

  if (loading) return <div className="loading-screen">Verifying access...</div>;

  // 1. Must be logged in
  if (!user) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // 2. Must be email verified
  if (!isEmailVerified) {
    alert("Please verify your email address to access your account.");
    auth.signOut(); 
    return <Navigate to="/signin" replace />;
  }

  // 3. Activation Check
  // We allow access to the children IF the user is activated OR if they are currently on the payment page.
  const isPaymentPath = location.pathname === '/payment-instructions';
  
  if (!isActivated && !isPaymentPath) {
    // If not activated and trying to go elsewhere (like /home), force them to pay
    return <Navigate to="/payment-instructions" replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, userData, loading } = useAuth();
  if (loading) return <div className="loading-screen">Verifying permissions...</div>;
  return (user && userData?.role === 'admin') ? children : <Navigate to="/home" replace />;
};

// --- MAIN APP COMPONENT ---
function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* PUBLIC ROUTES */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
          <Route path="/signin" element={<PublicRoute><SignIn /></PublicRoute>} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* PROTECTED ROUTES */}
          {/* Wrap PaymentInstructions in AuthRoute so it can access auth.currentUser */}
          <Route path="/payment-instructions" element={<AuthRoute><PaymentInstructions /></AuthRoute>} />
          
          <Route path="/home" element={<AuthRoute><HomeScreen /></AuthRoute>} />
          <Route path="/profile" element={<AuthRoute><ProfileScreen /></AuthRoute>} />
          <Route path="/settings" element={<AuthRoute><SettingsScreen /></AuthRoute>} />
          <Route path="/matches" element={<AuthRoute><MatchesScreen /></AuthRoute>} />
          <Route path="/messages" element={<AuthRoute><MessagesScreen /></AuthRoute>} />
          <Route path="/chat/:chatId" element={<AuthRoute><ChatScreen /></AuthRoute>} />
          <Route path="/post/:id" element={<AuthRoute><Post /></AuthRoute>} />

          {/* ADMIN ROUTES */}
          <Route path="/admin" element={<AdminRoute><AdminPortal /></AdminRoute>} />
          <Route path="/admin/user/:userId" element={<AdminRoute><UserDetails /></AdminRoute>} />

          {/* PAYMENT CALLBACKS */}
          <Route path="/response" element={
            <>
              <CybersourcePostHandler />
              <PaymentResponseScreen />
            </>
          } />

          {/* 404 FALLBACK */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;